import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { StarIcon } from '@heroicons/react/24/solid';

const Hero: React.FC = () => {
  const subtitle = 'LEVERAGE THE MOMENTS WITH OUR TOUCH';
  const promoParagraph = 'Elevate your business and personal moments with our top-tier photographers. Book your first session and enjoy an exclusive 25% discount. Capture your moment with timeless elegance';

  // Sequential typewriter states for both lines
  const fullLineOne = 'YOUR STORY';
  const fullLineTwo = 'OUR LENS';
  const [typedOne, setTypedOne] = useState('');
  const [typedTwo, setTypedTwo] = useState('');
  const [lineOneDone, setLineOneDone] = useState(false);
  const [lineTwoDone, setLineTwoDone] = useState(false);

  // Type first line
  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      if (i <= fullLineOne.length) {
        setTypedOne(fullLineOne.slice(0, i));
        i += 1;
      } else {
        clearInterval(interval);
        setLineOneDone(true);
      }
    }, 80);
    return () => clearInterval(interval);
  }, []);

  // Then type second line
  useEffect(() => {
    if (!lineOneDone) return;
    let j = 0;
    const interval = setInterval(() => {
      if (j <= fullLineTwo.length) {
        setTypedTwo(fullLineTwo.slice(0, j));
        j += 1;
      } else {
        clearInterval(interval);
        setLineTwoDone(true);
      }
    }, 80);
    return () => clearInterval(interval);
  }, [lineOneDone]);

  const scrollToSection = (sectionId: string) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="w-full h-full bg-cover bg-center bg-no-repeat filter grayscale hero-bg"
        />
        <div className="absolute inset-0 bg-black/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 text-center text-white px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
        {/* Main Typography */}
        <div className="mb-8">
          {/* Main Heading Lines */}
          <div className="text-4xl sm:text-6xl lg:text-7xl font-bold mb-8 leading-tight">
            <motion.div 
              initial={{ opacity: 0, y: 10 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }}
              className="mb-2"
            >
              <span>{typedOne}</span>
              {!lineOneDone && (
                <motion.span
                  className="inline-block w-0.5 h-10 sm:h-14 lg:h-16 bg-white ml-1 align-middle"
                  animate={{ opacity: [0, 1, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
              )}
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 10 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6, delay: 0.05 }}
            >
              <span>{typedTwo}</span>
              {lineOneDone && !lineTwoDone && (
                <motion.span
                  className="inline-block w-0.5 h-10 sm:h-14 lg:h-16 bg-white ml-1 align-middle"
                  animate={{ opacity: [0, 1, 0] }}
                  transition={{ duration: 1, repeat: Infinity }}
                />
              )}
            </motion.div>
          </div>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-lg sm:text-xl lg:text-2xl font-light text-gray-200 max-w-2xl mx-auto mb-8"
          >
            {subtitle}
          </motion.p>

          {/* Promotional Paragraph */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="text-base sm:text-lg lg:text-xl text-gray-200/90 max-w-3xl mx-auto text-center"
          >
            {promoParagraph}
          </motion.p>
        </div>

        {/* Rating */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="flex items-center justify-center space-x-1 mb-4"
        >
          {[...Array(5)].map((_, i) => (
            <StarIcon key={i} className="h-5 w-5 text-yellow-400" />
          ))}
          <span className="ml-2 text-gray-200">Trusted by 500+ clients</span>
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full max-w-xl mx-auto"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection('#booking')}
            className="bg-white text-black px-8 py-3 rounded-full font-semibold text-lg hover:bg-gray-100 transition-colors duration-200 shadow-lg w-full sm:w-auto"
          >
            BOOK MY SHOOT
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection('#portfolio')}
            className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold text-lg hover:bg-white hover:text-black transition-all duration-200 w-full sm:w-auto"
          >
            VIEW PORTFOLIO
          </motion.button>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-white rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
